import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import AppData from 'Data/AppData';
import { useUserContext } from 'Data/context/Users';
import { user_roles } from 'Data/Enum';
import UserData from 'Data/UserData';
import joi from 'joi';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useValidation } from 'Lib/hooks/useValidation';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { isUserAuthorized } from 'Util';

let DM = new DataManager();

const UserInformationPanel = ({
  userId,
  userObject,
  title = 'User',
  editable = false,
  ...props
}) => {
  const {
    currentObject: user,
    hasBeenUpdated,
    reset: resetUser,
    updateField,
    originalObject: originalUser,
    doSave,
  } = useUpdatableObject(UserData.updateRecord, UserData.createUser);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalUser,
    undoFunction: UserData.updateRecord,
    onUndo: (originalUser) => fetchUser(originalUser.id),
    objectType: 'User',
  });

  const { fullUserList: currentUsernameList } = useUserContext();

  useEffect(() => {
    DM.add(
      UserData.registerListener('BLANK_RECORD_LOADED', onUserLoaded),
      UserData,
    );
    DM.add(UserData.registerListener('RECORD_LOADED', onUserLoaded), UserData);
    DM.add(
      UserData.registerListener('RECORD_CREATED', onUserCreated),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_UPDATED', onUserUpdated),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const UNIDFormatErrorMessage =
    'User ID should be a university UNID starting with a lowercase u';

  let fields = {
    full_name: {
      label: 'Full Name',
      onEdit: (newValue) => {
        updateField('full_name', newValue);
      },
      isRequired: true,
    },

    ...((isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) ||
      user?.id === AppData.userID()) && {
      username: {
        label: 'User ID (UNID)',
        fieldSchema: joi
          .string()
          .label('User ID')
          .min(2)
          .pattern(new RegExp(/^u\d+$/))
          .invalid(
            ...currentUsernameList
              .filter(({ id }) => user?.id !== id)
              .map(({ username }) => username),
          )
          .insensitive()
          .required()
          .messages({
            'string.pattern.base': UNIDFormatErrorMessage,
            'string.empty': 'User ID is required',
            'any.invalid': 'User ID already in use',
            'string.min': UNIDFormatErrorMessage,
          }),
        onEdit: (newValue) => {
          updateField('username', newValue.toLowerCase());
        },
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
        isRequired: true,
      },
    }),

    emails: {
      label: 'Email',
      onEdit: (newValue) => {
        updateField('emails', newValue);
      },
      isRequired: true,
      isEmail: true,
    },

    job_title: {
      label: 'Position',
      onEdit: (newValue) => {
        updateField('job_title', newValue);
      },
    },

    office_phone: {
      label: 'Office Phone',
      value: user.phone_numbers?.office,
      onEdit: (newValue) => {
        updateField('phone_numbers', {
          ...user.phone_numbers,
          office: newValue,
        });
      },
    },

    ...((isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) ||
      user?.id === AppData.userID()) && {
      cell_phone: {
        label: 'Cell Phone',
        value: user.phone_numbers?.cell,
        onEdit: (newValue) => {
          updateField('phone_numbers', {
            ...user.phone_numbers,
            cell: newValue,
          });
        },
      },
    }),

    ...((isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) ||
      user?.id === AppData.userID()) && {
      home_phone: {
        label: 'Home Phone',
        value: user.phone_numbers?.home,
        onEdit: (newValue) => {
          updateField('phone_numbers', {
            ...user.phone_numbers,
            home: newValue,
          });
        },
      },
    }),

    ...(isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR],
    }) && {
      notes: {
        label: 'Notes',
        value: user.notes,
        onEdit: (newValue) => {
          updateField('notes', newValue);
        },
      },
    }),
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, user);

  useEffect(() => {
    if (userId && userId >= 0) {
      fetchUser(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (userObject) {
      if (!editable) {
        resetUser(userObject);
      } else if (userObject?.id && Number(userObject.id) >= 0) {
        fetchUser(userObject.id);
      }
    }
  }, [userObject]);

  const onUserLoaded = (payload) => {
    if (payload) {
      resetUser(payload);
      resetUndo(() => fetchUser(payload.id));
      resetValidation();
    }
  };

  const fetchUser = (id) => {
    UserData.getRecord(id, {
      _include: 'addresses',
    });
  };

  const onUserUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  const onUserCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('User Created');
      fetchUser(payload.id);
    }
  };

  const undoAction = () => {
    fetchUser(user.id);
    resetValidation();
  };

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible
      renderChild={getValidatedDetailTable}
      {...props}
    />
  );
};

UserInformationPanel.propTypes = {
  userId: PropTypes.number,
  userObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  hideNotes: PropTypes.bool,
};

export default UserInformationPanel;
